import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { timeAgo } from "@/lib/meta";
import { Receipt as ReceiptIcon, ExternalLink, ArrowLeft, CheckCircle2, Loader2 } from "lucide-react";

const short = (h) => (!h ? "" : h.length > 20 ? `${h.slice(0, 12)}…${h.slice(-8)}` : h);

export default function Receipt() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get(`/billing/receipt/${id}`)
      .then(({ data }) => setData(data))
      .catch((e) => setError(e?.response?.data?.detail || "Receipt not found"));
  }, [id]);

  if (error) {
    return (
      <div className="mx-auto max-w-lg px-5 py-16 text-center">
        <p className="text-dust" data-testid="receipt-error">{error}</p>
        <Link to="/app/billing"><Button variant="outline" className="mt-4 border-line bg-surface">Back to Billing</Button></Link>
      </div>
    );
  }
  if (!data) {
    return <div className="mx-auto max-w-lg px-5 py-16 text-center text-dust"><Loader2 className="mx-auto animate-spin" /></div>;
  }

  const t = data.transaction;
  const isRefill = t.kind === "refill";
  const label = t.pack === "custom" ? "Top-up" : t.pack === "auto-refill" ? "Auto-refill" : `${t.pack} pack`;

  return (
    <div className="mx-auto max-w-lg px-5 py-8 lg:px-8" data-testid="receipt-page">
      <Link to="/app/billing" className="mb-4 inline-flex items-center gap-1.5 text-sm text-dust hover:text-cream">
        <ArrowLeft size={15} /> Billing
      </Link>

      <div className="overflow-hidden rounded-2xl border border-line bg-surface">
        <div className="flex items-center gap-3 border-b border-line bg-raised/40 px-6 py-5">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-juice/10 text-juice">
            <ReceiptIcon size={22} />
          </div>
          <div>
            <h1 className="text-lg font-semibold tracking-tight">Payment receipt</h1>
            <p className="font-mono text-xs text-dust">{isRefill ? label : t.kind}</p>
          </div>
          <span className={`ml-auto inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold ${t.simulated === false ? "bg-pulp/10 text-pulp" : "bg-raised text-dust"}`} data-testid="receipt-status">
            <CheckCircle2 size={12} /> {t.simulated === false ? "On-chain" : "Simulated"}
          </span>
        </div>

        <dl className="divide-y divide-line/60 px-6 text-sm">
          <Row k="Amount"><span className="font-mono text-cream" data-testid="receipt-amount">${t.amount}</span></Row>
          {t.currency && <Row k="Paid in"><span className="font-mono text-pulp">{t.crypto_amount} {t.currency}</span></Row>}
          {data.chain && <Row k="Network"><span className="text-cream">{data.chain}</span></Row>}
          <Row k="Date"><span className="text-dust">{timeAgo(t.created_at)}</span></Row>
          <Row k="Transaction"><span className="font-mono text-[12px] text-cream">{short(t.tx_hash)}</span></Row>
        </dl>

        <div className="border-t border-line px-6 py-4">
          {data.explorer_url ? (
            <a href={data.explorer_url} target="_blank" rel="noreferrer" data-testid="receipt-explorer">
              <Button className="w-full gap-2 bg-juice font-semibold text-ink hover:bg-juice/90">
                View on explorer <ExternalLink size={15} />
              </Button>
            </a>
          ) : (
            <p className="text-center text-xs text-dust" data-testid="receipt-simulated-note">
              Simulated payment — no on-chain explorer link. Connect a real wallet to settle on-chain.
            </p>
          )}
        </div>
      </div>

      <p className="mt-4 text-center text-xs text-dust/70">70% creator · 15% protocol · 15% buy+burn $MOON — off the pad creator fee.</p>
    </div>
  );
}

const Row = ({ k, children }) => (
  <div className="flex items-center justify-between py-3.5">
    <dt className="text-dust">{k}</dt>
    <dd>{children}</dd>
  </div>
);
